// Country-aware address field configuration.
//
// The address block (City / State / Pincode) was hardcoded to India everywhere:
// an Indian state dropdown, a "Mumbai" city placeholder, a "Pincode" label and a
// 6-digit rule. On an Omani tenant that offered Andhra Pradesh and rejected a
// real 3-digit Omani code.
//
// The country comes from the tenant's immutable currency (`useTenantCountry()`),
// the same source the dial code and tax regime already use.
//
// India keeps its OWN option list, passed in by the caller. There are two of
// them — `constants/indian-states.ts` for locations and
// `features/suppliers/constants.ts` for suppliers — and they disagree on both
// spelling and ordering. Those strings are stored on rows, and location values
// feed the CGST/SGST-vs-IGST comparison, so neither may change.

import {
  getExampleCity,
  getPostalCodeRule,
  getRegionLabel,
  getRegionsForCountry,
  isValidPostalCode,
} from '@bbpos/types';
import type { PostalCodeRule, TenantCountry } from '@bbpos/types';

export interface SelectOption {
  value: string;
  label: string;
}

export interface AddressFields {
  /** "State", "Emirate", "Governorate", … */
  regionLabel: string;
  regionOptions: SelectOption[];
  regionPlaceholder: string;
  cityPlaceholder: string;
  /** "Pincode", "Postal Code", "PO Box". */
  postalLabel: string;
  postalPlaceholder: string;
  postal: PostalCodeRule;
  /** True where the country has no postal-code system (UAE, Qatar). */
  postalIsFreeForm: boolean;
}

/**
 * @param country       the tenant's (`useTenantCountry()`) or a supplier's own country; only `iso` is read
 * @param indiaOptions  the caller's existing India list, used verbatim for IN
 */
export function addressFieldsFor(
  country: Pick<TenantCountry, 'iso'>,
  indiaOptions: readonly SelectOption[],
): AddressFields {
  const { iso } = country;
  const label = getRegionLabel(iso);
  const postal = getPostalCodeRule(iso);

  const regionOptions =
    iso === 'IN'
      ? [...indiaOptions]
      : getRegionsForCountry(iso).map((r) => ({ value: r, label: r }));

  return {
    regionLabel: label,
    regionOptions,
    regionPlaceholder: `Select ${label.toLowerCase()}`,
    cityPlaceholder: getExampleCity(iso),
    postalLabel: postal.label,
    postalPlaceholder: postal.example,
    postal,
    postalIsFreeForm: postal.digits === null,
  };
}

/**
 * Keystroke filter for the postal input. India and the Gulf codes are numeric
 * and capped at their own length; a PO Box is free text, so it is left alone.
 * Previously every site did `.replace(/\D/g, '').slice(0, 6)`, which silently
 * truncated anything longer and stripped a PO Box to nothing.
 */
export function clampPostalInput(raw: string, fields: AddressFields): string {
  // The column is VARCHAR(10) on tenants, locations and suppliers alike.
  if (fields.postalIsFreeForm) return raw.slice(0, 10);
  const max = Math.max(...(fields.postal.digits ?? [6]));
  return raw.replace(/\D/g, '').slice(0, max);
}

/** Re-exported so call sites validate through one path. */
export { isValidPostalCode };
